import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import CreditPackages from './CreditPackages';
import CreditAnalytics from './CreditAnalytics';
import CreditTransactions from './CreditTransactions';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CreditManagement = ({ adminToken }) => {
  const { currentColors, colorTheme } = useColorTheme();
  
  // State management
  const [activeSubTab, setActiveSubTab] = useState('pricing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pricing config state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [editingPricing, setEditingPricing] = useState(false);
  
  // Credit packages state
  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showAddPackage, setShowAddPackage] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({ skip: 0, limit: 50 });

  useEffect(() => {
    if (activeSubTab === 'pricing') {
      fetchPricingConfig();
    } else if (activeSubTab === 'packages') {
      fetchPackages();
    } else if (activeSubTab === 'analytics') {
      fetchAnalytics();
    } else if (activeSubTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeSubTab]);

  const fetchPricingConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/credits/pricing-config`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPricingConfig(response.data);
    } catch (err) {
      setError('Failed to load pricing configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updatePricingConfig = async (config) => {
    try {
      setLoading(true);
      await axios.post(`${API}/api/admin/credits/pricing-config`, config, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSuccess('Pricing configuration updated successfully');
      setEditingPricing(false);
      fetchPricingConfig();
    } catch (err) {
      setError('Failed to update pricing configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/credits/credit-packages`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPackages(response.data.packages || []);
    } catch (err) {
      setError('Failed to load credit packages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createPackage = async (packageData) => {
    try {
      setLoading(true);
      await axios.post(`${API}/api/admin/credits/credit-packages`, packageData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSuccess('Credit package created successfully');
      setShowAddPackage(false);
      fetchPackages();
    } catch (err) {
      setError('Failed to create credit package');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updatePackage = async (packageId, packageData) => {
    try {
      setLoading(true);
      await axios.put(`${API}/api/admin/credits/credit-packages/${packageId}`, packageData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSuccess('Credit package updated successfully');
      setEditingPackage(null);
      fetchPackages();
    } catch (err) {
      setError('Failed to update credit package');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this credit package?')) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/api/admin/credits/credit-packages/${packageId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSuccess('Credit package deleted successfully');
      fetchPackages();
    } catch (err) {
      setError('Failed to delete credit package');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/credits/credit-analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/credits/credit-transactions?limit=${transactionsPagination.limit}&skip=${transactionsPagination.skip}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-clear messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">💳 Credit System Management</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Configure pricing, manage packages, and track usage
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 dark:bg-green-900 border border-green-400 text-green-700 dark:text-green-200 rounded-lg">
          {success}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="mb-6">
        <nav className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
          {[
            { key: 'pricing', label: '💰 Pricing Config', icon: '💰' },
            { key: 'packages', label: '📦 Credit Packages', icon: '📦' },
            { key: 'analytics', label: '📊 Analytics', icon: '📊' },
            { key: 'transactions', label: '📝 Transactions', icon: '📝' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.key
                  ? `${colorTheme === 'purple' ? 'border-purple-500 text-purple-600 dark:text-purple-400' : 
                      colorTheme === 'blue' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 
                      'border-green-500 text-green-600 dark:text-green-400'}`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
              colorTheme === 'purple' ? 'border-purple-600' : 
              colorTheme === 'blue' ? 'border-blue-600' : 
              'border-green-600'
            }`}></div>
          </div>
        ) : (
          <>
            {activeSubTab === 'pricing' && (
              <PricingConfigSection
                config={pricingConfig}
                editing={editingPricing}
                setEditing={setEditingPricing}
                onUpdate={updatePricingConfig}
                colorTheme={colorTheme}
              />
            )}
            
            {activeSubTab === 'packages' && (
              <CreditPackages
                packages={packages}
                editingPackage={editingPackage}
                setEditingPackage={setEditingPackage}
                showAddPackage={showAddPackage}
                setShowAddPackage={setShowAddPackage}
                onCreate={createPackage}
                onUpdate={updatePackage}
                onDelete={deletePackage}
                colorTheme={colorTheme}
              />
            )}
            
            {activeSubTab === 'analytics' && (
              <CreditAnalytics analytics={analytics} colorTheme={colorTheme} />
            )}
            
            {activeSubTab === 'transactions' && (
              <CreditTransactions 
                transactions={transactions} 
                pagination={transactionsPagination}
                setPagination={setTransactionsPagination}
                onRefresh={fetchTransactions}
                colorTheme={colorTheme} 
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Pricing Config Section Component
const PricingConfigSection = ({ config, editing, setEditing, onUpdate, colorTheme }) => {
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  // Helper function to safely parse integer input
  const handleNumberChange = (field, value) => {
    // Allow empty string for typing, store as empty or 0
    const numValue = value === '' ? '' : parseInt(value, 10);
    setFormData({...formData, [field]: isNaN(numValue) ? '' : numValue});
  };

  // Get display value for number inputs
  const getDisplayValue = (field, defaultVal = 0) => {
    const val = formData[field];
    if (val === '' || val === undefined || val === null) return '';
    return val;
  };

  if (!config) return <div>Loading configuration...</div>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      // Convert empty strings to default values before saving
      const dataToSave = {};
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (typeof val === 'string' && val === '') {
          dataToSave[key] = 0;
        } else if (val !== undefined && val !== null) {
          dataToSave[key] = val;
        }
      });
      
      await onUpdate(dataToSave);
      setSaveMessage({ type: 'success', text: 'Configuration saved successfully!' });
      setEditing(false);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Failed to save configuration. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Pricing Configuration</h2>
        <button
          onClick={() => setEditing(!editing)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            colorTheme === 'purple' 
              ? 'bg-purple-600 hover:bg-purple-700 text-white' 
              : colorTheme === 'blue'
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {editing ? 'Cancel' : 'Edit Configuration'}
        </button>
      </div>

      {saveMessage && (
        <div className={`mb-4 p-4 rounded-lg ${saveMessage.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {saveMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Ad Costs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            📝 Basic Ad Credits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Text Ad Credits
              </label>
              <input
                type="number"
                min="0"
                value={getDisplayValue('text_ad_credits', 3)}
                onChange={(e) => handleNumberChange('text_ad_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <p className="text-xs text-gray-500 mt-1">Social media, Google ads, emails</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Image Ad Credits
              </label>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_ad_credits', 15)}
                onChange={(e) => handleNumberChange('image_ad_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <p className="text-xs text-gray-500 mt-1">AI-generated images</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Complete Campaign Credits
              </label>
              <input
                type="number"
                min="0"
                value={getDisplayValue('complete_campaign_credits', 65)}
                onChange={(e) => handleNumberChange('complete_campaign_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <p className="text-xs text-gray-500 mt-1">Full campaign generation</p>
            </div>
          </div>
        </div>

        {/* Video Model Pricing */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            🎬 Video Ad Credits (Per Model)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Configure credits for different video generation models and quality levels
          </p>
          
          {/* Fast Video (Veo 3.1 Fast) Section */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              🚀 Fast Video (4-8s)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Fast Video 720p */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-blue-200 dark:bg-blue-800 px-2 py-0.5 rounded">720p</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Standard HD</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_fast_credits', 50)}
                  onChange={(e) => handleNumberChange('video_veo3_fast_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
              
              {/* Fast Video 1080p */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-blue-300 dark:bg-blue-700 px-2 py-0.5 rounded">1080p</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Full HD</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_fast_1080p_credits', 60)}
                  onChange={(e) => handleNumberChange('video_veo3_fast_1080p_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
              
              {/* Fast Video 4K */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-blue-400 dark:bg-blue-600 px-2 py-0.5 rounded text-white">4K</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Ultra HD (~7-13 min)</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_fast_4k_credits', 100)}
                  onChange={(e) => handleNumberChange('video_veo3_fast_4k_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
            </div>
          </div>
          
          {/* Premium Video (Veo 3.1) Section */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              🎥 Premium Video (4-8s)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Premium Video 720p */}
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-purple-200 dark:bg-purple-800 px-2 py-0.5 rounded">720p</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Standard HD</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_credits', 50)}
                  onChange={(e) => handleNumberChange('video_veo3_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
              
              {/* Premium Video 1080p */}
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-purple-300 dark:bg-purple-700 px-2 py-0.5 rounded">1080p</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Full HD</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_1080p_credits', 60)}
                  onChange={(e) => handleNumberChange('video_veo3_1080p_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
              
              {/* Premium Video 4K */}
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-purple-400 dark:bg-purple-600 px-2 py-0.5 rounded text-white">4K</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Ultra HD (~7-13 min)</p>
                <input
                  type="number"
                  min="0"
                  value={getDisplayValue('video_veo3_4k_credits', 100)}
                  onChange={(e) => handleNumberChange('video_veo3_4k_credits', e.target.value)}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
            </div>
          </div>
          
          {/* Other Video Models */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Quick Video */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">✨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Quick Video</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Fast generation, Freepik</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('video_freepik_credits', 50)}
                onChange={(e) => handleNumberChange('video_freepik_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Ultra Video (720p) */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎬</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Ultra Video</h4>
                <span className="text-xs bg-orange-200 dark:bg-orange-800 px-2 py-0.5 rounded">720p</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">10-15s, with native audio</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('video_sora2_standard_credits', 50)}
                onChange={(e) => handleNumberChange('video_sora2_standard_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Ultra Video (1080p) */}
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎬</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Ultra Video</h4>
                <span className="text-xs bg-red-200 dark:bg-red-800 px-2 py-0.5 rounded">1080p HD</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">10-15s, HD with native audio</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('video_sora2_pro_credits', 75)}
                onChange={(e) => handleNumberChange('video_sora2_pro_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Image Ad Credits */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            🖼️ Image Ad Credits (Per Model)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Configure credits for different image generation models and quality levels
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Quick Image - Nebius */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">💨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Quick Image</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Very fast generation, Nebius</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_nebius_credits', 15)}
                onChange={(e) => handleNumberChange('image_nebius_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Standard Image - Kie AI */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Standard Image</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Good quality, Kie AI</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_kie_ai_credits', 15)}
                onChange={(e) => handleNumberChange('image_kie_ai_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Best Value - Freepik */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Best Value</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Premium quality, Freepik</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_freepik_credits', 15)}
                onChange={(e) => handleNumberChange('image_freepik_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Premium Image - Nano Banana Pro (1K/2K) */}
            <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">✨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Premium Image</h4>
                <span className="text-xs bg-pink-200 dark:bg-pink-800 px-2 py-0.5 rounded">1K/2K</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">HD resolution, Nano Banana Pro</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_nano_banana_pro_hd_credits', 18)}
                onChange={(e) => handleNumberChange('image_nano_banana_pro_hd_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Premium Image - Nano Banana Pro (4K) */}
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">✨</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Premium Image</h4>
                <span className="text-xs bg-red-200 dark:bg-red-800 px-2 py-0.5 rounded">4K Ultra</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">4K resolution, Nano Banana Pro</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_nano_banana_pro_4k_credits', 24)}
                onChange={(e) => handleNumberChange('image_nano_banana_pro_4k_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Ultra Image - GPT-4o */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🚀</span>
                <h4 className="font-medium text-gray-900 dark:text-white">Ultra Image</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Best quality, GPT-4o</p>
              <input
                type="number"
                min="0"
                value={getDisplayValue('image_gpt4o_credits', 20)}
                onChange={(e) => handleNumberChange('image_gpt4o_credits', e.target.value)}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Free Tier */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            🎁 Free Tier Limits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Campaigns Per Month
              </label>
              <input
                type="number"
                min="0"
                value={getDisplayValue('free_tier_campaigns_per_month', 1)}
                onChange={(e) => handleNumberChange('free_tier_campaigns_per_month', e.target.value)}
                disabled={!editing}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Text Ads Per Month
              </label>
              <input
                type="number"
                min="0"
                value={getDisplayValue('free_tier_text_ads_per_month', 10)}
                onChange={(e) => handleNumberChange('free_tier_text_ads_per_month', e.target.value)}
                disabled={!editing}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>

        {/* System Flags */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            ⚙️ System Controls
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Enable Credit System</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Show credits to users (not enforced)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.credit_system_enabled || false}
                  onChange={(e) => setFormData({...formData, credit_system_enabled: e.target.checked})}
                  disabled={!editing}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Enforce Credits</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Block generation when out of credits</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enforce_credits || false}
                  onChange={(e) => setFormData({...formData, enforce_credits: e.target.checked})}
                  disabled={!editing}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>
        </div>

        {editing && (
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => {
                setFormData(config);
                setEditing(false);
                setSaveMessage(null);
              }}
              className="px-6 py-3 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                colorTheme === 'purple' 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : colorTheme === 'blue'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-50`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreditManagement;
